import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { get, ref, update } from 'firebase/database';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import {
  FiArrowLeft,
  FiBriefcase,
  FiCheck,
  FiCheckCircle,
  FiFileText,
  FiLoader,
  FiLock,
  FiMapPin,
  FiUpload,
} from 'react-icons/fi';
import { auth, db, storage } from '../../firebaseConfig';
import { submitApplication } from '../applicationApi';
import { localizeEmploymentType, localizeWorkMode } from '../jobMetadataLocale';
import {
  answerHasValue,
  profileApplicationFields,
  PUBLIC_ATTACHMENT_MAX_BYTES,
  PUBLIC_CV_MAX_BYTES,
  resolvePublicApplicationConfig,
  safePublicJobId,
  safePublicStorageSegment,
  validLinkedInUrl,
} from '../publicJobApplication';
import './PublicJobApplicationScreen.css';
import { useLanguage } from '../contexts/LanguageContext';

function plainJobDescription(job) {
  const plain = String(job.jobDescription || job.description || job.roleDescription || '').trim();
  if (plain) return plain;
  const html = String(job.jobDescriptionHTML || '').trim();
  if (!html) return '';
  if (typeof document === 'undefined') return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const container = document.createElement('div');
  container.innerHTML = html;
  return String(container.textContent || '').replace(/\s+/g, ' ').trim();
}

function jobTitle(job, fallback = 'Job opportunity') {
  return String(job.jobOpening || job.title || job.jobTitle || fallback).trim();
}

function profileDisplayName(profile, user, fallback = 'Candidate') {
  const values = profileApplicationFields(profile);
  return [values.firstName, values.lastName].filter(Boolean).join(' ') || profile?.username || user?.email || fallback;
}

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

async function createOrReconnectAccount(email, password) {
  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    return { user: credential.user, created: true };
  } catch (createError) {
    if (createError?.code !== 'auth/email-already-in-use') throw createError;
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      return { user: credential.user, created: false };
    } catch (signInError) {
      const error = new Error('This email already has a JOBSEEQR account. Enter the correct password and try again.');
      error.cause = signInError;
      throw error;
    }
  }
}

function publicApplicationError(error, translate) {
  if (error?.message?.startsWith('This email already has a JOBSEEQR account.')) return translate('errors.existing');
  switch (error?.code) {
    case 'auth/weak-password':
      return translate('errors.weak');
    case 'auth/invalid-email':
      return translate('errors.email');
    case 'auth/too-many-requests':
      return translate('errors.attempts');
    case 'auth/network-request-failed':
      return translate('errors.network');
    default:
      return error?.message || translate('errors.submit');
  }
}

function fileExtension(file) {
  const extension = String(file?.name || '').split('.').pop();
  return /^[A-Za-z0-9]{1,8}$/.test(extension) ? extension.toLowerCase() : 'bin';
}

async function uploadOwnedFile({ userId, jobId, category, file }) {
  const storagePath = `Images/${userId}/applications/${jobId}/${category}.${fileExtension(file)}`;
  const target = storageRef(storage, storagePath);
  await uploadBytes(target, file, { contentType: file.type || 'application/octet-stream' });
  return {
    name: file.name,
    size: file.size,
    type: file.type || 'application/octet-stream',
    storagePath,
    downloadUrl: await getDownloadURL(target),
    uploadedAt: Date.now(),
  };
}

function QuestionField({ question, value, onChange, disabled, translate }) {
  const fieldId = `public-question-${safePublicStorageSegment(question.id)}`;
  const common = { id: fieldId, disabled };

  if (question.type === 'textarea') {
    return <textarea {...common} rows={4} value={value || ''} onChange={(event) => onChange(event.target.value)} />;
  }
  if (question.type === 'select') {
    return (
      <select {...common} value={value || ''} onChange={(event) => onChange(event.target.value)}>
        <option value="">{translate('select')}</option>
        {question.options.map((option) => <option value={option} key={option}>{option}</option>)}
      </select>
    );
  }
  if (question.type === 'radio') {
    const options = question.options.length ? question.options : [translate('yes'), translate('no')];
    return (
      <div className="public-apply-choices" id={fieldId}>
        {options.map((option) => (
          <label key={option}>
            <input
              type="radio"
              name={fieldId}
              value={option}
              checked={value === option}
              onChange={() => onChange(option)}
              disabled={disabled}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
    );
  }
  if (question.type === 'checkbox') {
    return (
      <div className="public-apply-choices" id={fieldId}>
        {question.options.map((option) => {
          const selected = Array.isArray(value) ? value : [];
          return (
            <label key={option}>
              <input
                type="checkbox"
                value={option}
                checked={selected.includes(option)}
                onChange={() => onChange(
                  selected.includes(option)
                    ? selected.filter((item) => item !== option)
                    : [...selected, option],
                )}
                disabled={disabled}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    );
  }
  if (question.type === 'file' || question.type === 'video') {
    return (
      <input
        {...common}
        type="file"
        accept={question.type === 'video' ? 'video/*' : 'application/pdf,.pdf'}
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
    );
  }
  return (
    <input
      {...common}
      type={question.type === 'url' ? 'url' : 'text'}
      value={value || ''}
      placeholder={question.type === 'url' ? 'https://' : ''}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

// This candidate-facing screen belongs in the job-seeker application. The
// WordPress page only links here and never receives credentials, files, or scores.
function PublicJobApplicationScreen({ user, profile = {}, onProfileSaved }) {
  const params = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const tp = useCallback((key) => t(`publicApplicationPage.${key}`), [t]);
  const fill = (template, values) => Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`{${key}}`, String(value)), template);
  const requestedJobId = safePublicJobId(params.jobId);
  const initialProfile = useMemo(() => profileApplicationFields(profile), [profile]);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [firstName, setFirstName] = useState(initialProfile.firstName);
  const [lastName, setLastName] = useState(initialProfile.lastName);
  const [linkedInProfile, setLinkedInProfile] = useState(initialProfile.linkedInProfile);
  const [email, setEmail] = useState(user?.email || profile?.email || '');
  const [password, setPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(Boolean(user));
  const [cvFile, setCvFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionNeedsToggle, setDescriptionNeedsToggle] = useState(false);
  const descriptionRef = useRef(null);
  const existingCv = profile?.cvMetadata?.storagePath ? profile.cvMetadata : null;

  useEffect(() => {
    setFirstName((current) => current || initialProfile.firstName);
    setLastName((current) => current || initialProfile.lastName);
    setLinkedInProfile((current) => current || initialProfile.linkedInProfile);
    setEmail((current) => user?.email || current || profile?.email || '');
    if (user) setAcceptedTerms(true);
  }, [initialProfile, profile?.email, user]);

  useEffect(() => {
    let active = true;
    const loadJob = async () => {
      if (!requestedJobId) {
        setLoadError(tp('errors.invalidLink'));
        setLoading(false);
        return;
      }
      try {
        const jobSnapshot = await get(ref(db, `job_posts/${requestedJobId}`));
        const applicationSnapshot = user?.uid
          ? await get(ref(db, `people/${user.uid}/application_index/applications/${requestedJobId}`))
          : null;
        if (!active) return;
        if (!jobSnapshot.exists()) {
          setLoadError(tp('errors.unavailable'));
          return;
        }
        const loaded = { id: requestedJobId, ...jobSnapshot.val() };
        if (loaded.published === false || loaded.active === false || loaded.status === 'closed') {
          setLoadError(tp('errors.closed'));
          return;
        }
        setJob(loaded);
        const application = applicationSnapshot?.val?.() || {};
        setSubmitted(Boolean(application.submittedAt) || application.status === 'submitted');
      } catch (loadFailure) {
        console.error('Failed to load public job application:', loadFailure);
        if (active) setLoadError(tp('errors.load'));
      } finally {
        if (active) setLoading(false);
      }
    };
    loadJob();
    return () => { active = false; };
  }, [requestedJobId, tp, user?.uid]);

  const applicationConfig = useMemo(() => resolvePublicApplicationConfig(job || {}), [job]);
  const jobDescription = useMemo(() => plainJobDescription(job || {}), [job]);

  // The public page keeps long job descriptions compact while preserving a
  // customer-controlled way to read the complete employer-provided copy.
  useEffect(() => {
    setDescriptionExpanded(false);
    setDescriptionNeedsToggle(false);
  }, [jobDescription]);

  useEffect(() => {
    if (!jobDescription || descriptionExpanded) return undefined;
    const descriptionElement = descriptionRef.current;
    if (!descriptionElement) return undefined;

    const measureDescription = () => {
      setDescriptionNeedsToggle(
        descriptionElement.scrollHeight > descriptionElement.clientHeight + 1,
      );
    };
    const timer = window.setTimeout(measureDescription, 0);
    window.addEventListener('resize', measureDescription);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('resize', measureDescription);
    };
  }, [descriptionExpanded, jobDescription]);

  const validate = () => {
    if (!firstName.trim() || !lastName.trim()) return tp('errors.name');
    if (!user && !validEmail(email)) return tp('errors.email');
    if (!user && password.length < 10) return tp('errors.weak');
    if (!user && !acceptedTerms) return tp('errors.terms');
    if (!validLinkedInUrl(linkedInProfile)) return tp('errors.linkedin');
    if (!existingCv && !cvFile) return tp('errors.cv');
    if (cvFile && (cvFile.type !== 'application/pdf' || cvFile.size > PUBLIC_CV_MAX_BYTES)) {
      return tp('errors.cvSize');
    }
    if (applicationConfig.coverLetterRequired && !coverLetterFile) return tp('errors.coverRequired');
    if (coverLetterFile && (coverLetterFile.type !== 'application/pdf' || coverLetterFile.size > PUBLIC_ATTACHMENT_MAX_BYTES)) {
      return tp('errors.coverSize');
    }
    const missingQuestion = applicationConfig.questions.find(
      (question) => question.required && !answerHasValue(question, answers[question.id]),
    );
    if (missingQuestion) return fill(tp('errors.answer'), { label: missingQuestion.label });
    const oversizedFile = applicationConfig.questions.find((question) => {
      const value = answers[question.id];
      return (question.type === 'file' || question.type === 'video')
        && value?.size > PUBLIC_ATTACHMENT_MAX_BYTES;
    });
    if (oversizedFile) return fill(tp('errors.fileSize'), { label: oversizedFile.label });
    return '';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let applicationUser = user;
      let accountCreated = false;
      const normalizedEmail = String(user?.email || email).trim().toLowerCase();

      if (!applicationUser) {
        const account = await createOrReconnectAccount(normalizedEmail, password);
        applicationUser = account.user;
        accountCreated = account.created;
      }

      let cvMetadata = existingCv;
      if (cvFile) {
        const cvPath = `Images/${applicationUser.uid}/cv.pdf`;
        const target = storageRef(storage, cvPath);
        await uploadBytes(target, cvFile, { contentType: 'application/pdf' });
        cvMetadata = {
          name: cvFile.name,
          size: cvFile.size,
          type: cvFile.type,
          storagePath: cvPath,
          downloadUrl: await getDownloadURL(target),
          capturedAt: Date.now(),
        };
      }

      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      const personalInfo = {
        ...(profile.personalInfo || {}),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        fullName,
        linkedInProfile: linkedInProfile.trim(),
        email: normalizedEmail,
      };
      const profileUpdates = {
        username: fullName,
        email: normalizedEmail,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        linkedInProfile: linkedInProfile.trim(),
        personalInfo,
        ...(accountCreated ? {
          openToWork: true,
          onboardingStatus: 'pending',
          source: 'wordpress-public-job',
          createdAt: Date.now(),
        } : {}),
        ...(cvMetadata ? {
          cvMetadata,
          cv: { ...(profile.cv || {}), Link: cvMetadata.downloadUrl || profile.cv?.Link || '' },
          cvUrl: cvMetadata.downloadUrl || profile.cvUrl || '',
        } : {}),
      };

      if (onProfileSaved) {
        await onProfileSaved(profileUpdates, applicationUser);
      } else {
        // New and returning public applicants both reach this branch. The
        // password stays exclusively inside Firebase Authentication.
        await update(ref(db, `people/${applicationUser.uid}`), profileUpdates);
      }

      const documents = {};
      if (coverLetterFile) {
        documents.coverLetter = await uploadOwnedFile({
          userId: applicationUser.uid, jobId: requestedJobId, category: 'cover-letter', file: coverLetterFile,
        });
      }

      const applicationResponses = [];
      for (const question of applicationConfig.questions) {
        let answer = answers[question.id];
        if ((question.type === 'file' || question.type === 'video') && answer) {
          answer = await uploadOwnedFile({
            userId: applicationUser.uid,
            jobId: requestedJobId,
            category: `response-${safePublicStorageSegment(question.id)}`,
            file: answer,
          });
        }
        applicationResponses.push({
          id: question.id,
          question: question.label,
          type: question.type,
          systemKey: question.systemKey,
          answer: answer ?? '',
        });
      }

      await submitApplication({
        user: applicationUser,
        jobId: requestedJobId,
        checklist: { submitApplication: true },
        documents,
        applicationResponses,
        applicationProfile: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          linkedInProfile: linkedInProfile.trim(),
        },
        source: 'wordpress-public-job',
      });
      setSubmitted(true);
    } catch (submitError) {
      console.error('Failed to submit public job application:', submitError);
      setError(publicApplicationError(submitError, tp));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className="public-apply-state"><FiLoader className="is-spinning" /><p>{tp('loading')}</p></main>;
  }
  if (loadError || !job) {
    return (
      <main className="public-apply-state is-error">
        <h1>{tp('unavailable')}</h1>
        <p>{loadError || tp('notFound')}</p>
        <button type="button" onClick={() => navigate('/opportunities')}>{tp('browse')}</button>
      </main>
    );
  }
  if (submitted) {
    return (
      <main className="public-apply-state is-success">
        <FiCheckCircle />
        <p className="public-apply-eyebrow">{tp('submitted')}</p>
        <h1>{jobTitle(job, tp('jobOpportunity'))}</h1>
        <p>{fill(tp('received'), { company: job.companyName || tp('hiringTeam') })}</p>
        <div className="public-apply-state__actions">
          <button type="button" onClick={() => navigate('/inbox')}>{tp('view')}</button>
          <button type="button" className="is-secondary" onClick={() => navigate('/opportunities')}>{tp('browseMore')}</button>
        </div>
      </main>
    );
  }

  return (
    <main className="public-apply-page">
      <header className="public-apply-topbar">
        <p className="public-apply-brand" dir="ltr" aria-label="JOBSEEQR">
          <span>JOBS</span>
          <span>EEQR</span>
        </p>
        <span className="public-apply-secure"><FiLock /> {tp('secure')}</span>
      </header>

      <section className="public-apply-shell">
        <form className="public-apply-form" onSubmit={handleSubmit}>
          <section className="public-apply-job-summary">
            <p className="public-apply-eyebrow">{tp('applyingFor')}</p>
            <h1>{jobTitle(job, tp('jobOpportunity'))}</h1>
            <p className="public-apply-job-company">{job.companyName || tp('employer')}</p>
          <div className="public-apply-job-facts">
            {job.companyLocation && <span><FiMapPin />{job.companyLocation}</span>}
            {(job.workspace || job.schedule) && (
              <span>
                <FiBriefcase />
                {[
                  localizeEmploymentType(job.schedule, language),
                  localizeWorkMode(job.workspace, language),
                ].filter(Boolean).join(' · ')}
              </span>
            )}
          </div>
          {jobDescription && (
            <div className="public-apply-job-description-wrap">
              <p
                ref={descriptionRef}
                className={`public-apply-job-description ${descriptionExpanded ? 'is-expanded' : ''}`}
              >
                {jobDescription}
              </p>
              {descriptionNeedsToggle && (
                <button
                  className="public-apply-description-toggle"
                  type="button"
                  onClick={() => setDescriptionExpanded((current) => !current)}
                >
                  {descriptionExpanded ? tp('showLess') : tp('readFull')}
                </button>
              )}
            </div>
          )}
          </section>

          <div className="public-apply-form__heading">
            <p className="public-apply-eyebrow">{tp('application')}</p>
            <h2>{user ? tp('ready') : tp('apply')}</h2>
            <p>{user
              ? fill(tp('savedProfile'), { company: job.companyName || tp('employer') })
              : tp('createAccount')}</p>
            <p className="public-apply-account-note">
              <FiLock />
              {user
                ? fill(tp('applyingAs'), { name: profileDisplayName(profile, user, tp('candidate')), email: user.email })
                : tp('existingAccount')}
            </p>
          </div>
          {error && <div className="public-apply-error" role="alert">{error}</div>}

          {!user && (
            <>
              <section className="public-apply-section">
                <h3>{tp('details')}</h3>
                <div className="public-apply-grid">
                  <label><span>{tp('firstName')}</span><input value={firstName} onChange={(event) => setFirstName(event.target.value)} disabled={submitting} /></label>
                  <label><span>{tp('lastName')}</span><input value={lastName} onChange={(event) => setLastName(event.target.value)} disabled={submitting} /></label>
                  <label>
                    <span>{tp('email')}</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      disabled={submitting}
                    />
                  </label>
                  <label>
                    <span>{tp('password')}</span>
                    <input
                      type="password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      placeholder={tp('passwordPlaceholder')}
                      disabled={submitting}
                    />
                    <small className="public-apply-field-help">{tp('passwordHelp')}</small>
                  </label>
                  <label className="is-wide"><span>{tp('linkedin')}</span><input type="url" value={linkedInProfile} onChange={(event) => setLinkedInProfile(event.target.value)} placeholder="https://www.linkedin.com/in/your-profile" disabled={submitting} /></label>
                </div>
              </section>

              <section className="public-apply-section">
                <h3>{tp('cvResume')}</h3>
                <label className="public-apply-upload">
                  <FiFileText />
                  <span><strong>{cvFile?.name || tp('uploadCv')}</strong><small>{tp('pdf10')}</small></span>
                  <FiUpload />
                  <input type="file" accept="application/pdf,.pdf" onChange={(event) => setCvFile(event.target.files?.[0] || null)} disabled={submitting} />
                </label>
              </section>
            </>
          )}

          {user && !existingCv && (
            <section className="public-apply-section">
              <h3>{tp('addCv')}</h3>
              <label className="public-apply-upload">
                <FiFileText />
                <span><strong>{cvFile?.name || tp('uploadCv')}</strong><small>{tp('pdf10')}</small></span>
                <FiUpload />
                <input type="file" accept="application/pdf,.pdf" onChange={(event) => setCvFile(event.target.files?.[0] || null)} disabled={submitting} />
              </label>
            </section>
          )}

          {applicationConfig.orderedFields.length > 0 && (
            <section className="public-apply-section">
              <h3>{fill(tp('questions'), { company: job.companyName || tp('employer') })}</h3>
              <div className="public-apply-questions">
                {applicationConfig.orderedFields.map((field) => (
                  field.systemKey === 'coverLetter' ? (
                    <div className="public-apply-dynamic-field" key={field.id}>
                      <span className="public-apply-field-label">{field.label}{field.required ? <em>*</em> : <small>{tp('optional')}</small>}</span>
                      <label className="public-apply-upload">
                        <FiFileText />
                        <span><strong>{coverLetterFile?.name || fill(tp('upload'), { label: field.label })}</strong><small>{tp('pdf20')}</small></span>
                        <FiUpload />
                        <input type="file" accept="application/pdf,.pdf" onChange={(event) => setCoverLetterFile(event.target.files?.[0] || null)} disabled={submitting} />
                      </label>
                    </div>
                  ) : (
                    <label key={field.id}>
                      <span>{field.label}{field.required && <em>*</em>}</span>
                      <QuestionField question={field} value={answers[field.id]} disabled={submitting} translate={tp} onChange={(value) => setAnswers((current) => ({ ...current, [field.id]: value }))} />
                    </label>
                  )
                ))}
              </div>
            </section>
          )}

          {!user && (
            <label className="public-apply-terms">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) => setAcceptedTerms(event.target.checked)}
                disabled={submitting}
              />
              <span>{tp('terms')}</span>
            </label>
          )}

          <footer className="public-apply-submit-row">
            <p><FiLock /> {fill(tp('secureShare'), { company: job.companyName || tp('employer') })}</p>
            <div className="public-apply-actions">
              <button type="button" className="is-secondary" onClick={() => navigate('/opportunities')} disabled={submitting}>
                <FiArrowLeft className="js-icon--directional" /> {tp('back')}
              </button>
              <button type="submit" disabled={submitting}>
                {submitting ? <><FiLoader className="is-spinning" /> {tp('submitting')}</> : <><FiCheck /> {tp('submit')}</>}
              </button>
            </div>
          </footer>
        </form>
      </section>
    </main>
  );
}

export default PublicJobApplicationScreen;
